import { observer } from 'mobx-react-lite'
import { CardString, Suit, Value } from 'pokersolver'
import { Animated, StyleProp, StyleSheet, Text, View, ViewStyle } from 'react-native'
import { color, fontFamily } from '../../../theme'
import UnrevealedCard from './assets/UnrevealedCard.svg'
import { useFlip } from './useFlip'
import { useStore } from '../../../models'
import { Palette } from '@ppwr/poker'
import { getDSColorWithOpacity } from '../../../design-system/Palette'
import { Icon } from '../../../design-system/Icon'
import { DSColor } from '@ppwr/poker'

import ClubColor2Icon from './assets/color2/club.svg'
import ClubColor4Icon from './assets/color4/club.svg'
import DiamondColor2Icon from './assets/color2/diamond.svg'
import DiamondColor4Icon from './assets/color4/diamond.svg'
import HeartColor2Icon from './assets/color2/heart.svg'
import HeartColor4Icon from './assets/color4/heart.svg'
import SpadeColor2Icon from './assets/color2/spade.svg'
import SpadeColor4Icon from './assets/color4/spade.svg'
import { SvgProps } from 'react-native-svg'
import { FC } from 'react'

type DegreeString = `${string}deg`
interface CardProps {
  card?: CardString
  fontSize: number
  horizontal?: boolean
  rotateDegrees?: DegreeString
  shadow?: boolean
  disabled?: boolean
  animate?: boolean
  makeSound?: boolean
  unrevealedCardColor?: UnrevealedCardColors
  isOnTable?: boolean
}

interface SuitProp {
  icon: FC<SvgProps>
  color: DSColor
}

interface IconsColor {
  c: SuitProp
  d: SuitProp
  s: SuitProp
  h: SuitProp
}

const IconsColor2: IconsColor = {
  c: { icon: ClubColor2Icon, color: 'Global/Black' },
  d: { icon: DiamondColor2Icon, color: 'Utility/Warning' },
  s: { icon: HeartColor2Icon, color: 'Global/Black' },
  h: { icon: SpadeColor2Icon, color: 'Utility/Warning' }
}

const IconsColor4: IconsColor = {
  c: { icon: ClubColor4Icon, color: 'Brand/Orange' },
  d: { icon: DiamondColor4Icon, color: 'Brand/Maroon' },
  s: { icon: HeartColor4Icon, color: 'Brand/Blue' },
  h: { icon: SpadeColor4Icon, color: 'Brand/Pink' }
}

const primaryAxisModificator = 3
const secondaryAxisModificator = 2

const CardsColors = {
  gray: getDSColorWithOpacity('Global/Black', 0.3),
  'light-purple': Palette['Brand/Maroon'],
  'dark-purple': '#54032A'
} as const

type UnrevealedCardColors = keyof typeof CardsColors

const UnrevealedCardComp = ({ color }: { color: UnrevealedCardColors }) => {
  return <UnrevealedCard height="100%" width="100%" color={color ? CardsColors[color] : CardsColors['dark-purple']} />
}

export const Card = observer(function Card({
  card,
  fontSize,
  horizontal,
  rotateDegrees,
  shadow,
  disabled = false,
  animate = true,
  makeSound = false,
  unrevealedCardColor = 'dark-purple',
  isOnTable = false
}: CardProps) {
  const {
    game: { isFourColoredDeckEnabled, isMyFirstGame }
  } = useStore()
  const isDisabled = disabled && !isOnTable
  const axisStyles: StyleProp<ViewStyle> = {
    flexDirection: horizontal ? 'row' : 'column',
    height: fontSize * (horizontal ? secondaryAxisModificator : primaryAxisModificator),
    width: fontSize * (horizontal ? primaryAxisModificator : secondaryAxisModificator),
    borderColor: isDisabled ? color.palette.blackWithOpacity.twenty : 'black'
  }
  const baseRotationStyles = rotateDegrees ? { transform: [{ rotate: rotateDegrees }] } : {}

  const { flipToBackStyle, flipToFrontStyle } = useFlip(Boolean(animate && card), makeSound)

  if (!card)
    return (
      <View style={baseRotationStyles}>
        <Animated.View
          style={[
            { opacity: isDisabled ? 0.5 : 1 },
            styles.container,
            axisStyles,
            shadow ? styles.shadow : {},
            animate ? flipToFrontStyle : {}
          ]}
        >
          <UnrevealedCardComp color={unrevealedCardColor} />
        </Animated.View>
      </View>
    )

  const [value, suit] = card as unknown as [Value, Suit]
  const actualValue = value === 'T' ? 10 : value

  const Svg = isFourColoredDeckEnabled || isMyFirstGame ? IconsColor4[suit].icon : IconsColor2[suit].icon

  return (
    <View style={baseRotationStyles}>
      <Animated.View
        style={[
          styles.container,
          axisStyles,
          disabled ? (isOnTable ? styles.withDisabledCardOnTable : styles.withDisabledCard) : styles.withCard,
          shadow ? styles.shadow : {},
          animate ? flipToBackStyle : {}
        ]}
      >
        <Text
          style={[
            styles.text,
            { fontSize: fontSize, color: isDisabled ? color.palette.blackWithOpacity.twenty : 'black' }
          ]}
        >
          {actualValue}
        </Text>
        <Svg height="40%" width="40%" preserveAspectRatio="xMidYMid meet" style={{ opacity: isDisabled ? 0.5 : 1 }} />
      </Animated.View>
    </View>
  )
})

const styles = StyleSheet.create({
  container: {
    borderRadius: 4,
    justifyContent: 'center',
    alignItems: 'center',
    display: 'flex',
    flexDirection: 'row'
    // height: 69,
    // width: '100%'
    // width: 45
  },
  withCard: {
    backgroundColor: color.palette.white
  },
  withDisabledCard: {
    backgroundColor: color.palette.white
  },
  withDisabledCardOnTable: {
    backgroundColor: `${color.palette.white}50`
  },
  horizontal: {
    flexDirection: 'row'
  },
  text: {
    fontFamily: fontFamily.FeatureDeckMedium,
    textAlignVertical: 'center' // TODO: text is not fully centered with icon.
  },
  shadow: {
    shadowColor: '#000',
    shadowOffset: {
      width: 0,
      height: 2
    },
    shadowOpacity: 0.25,
    shadowRadius: 3.84,
    elevation: 5
  }
})
